import React from 'react';
import { useMembershipProductShowContext } from '../../startup/MembershipProductShowApp';

const formatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
});

function breakdown(price) {
  if (!price || !price.amount_cents || price.amount_cents <= 0) return null;
  return {
    basePrice: price.amount_cents / 100,
    totalFee: (price.total_fee_amount_cents || 0) / 100,
    customerPays: (price.customer_pays_cents || 0) / 100,
    netToVenue: (price.net_to_venue_cents || 0) / 100,
  };
}

function BreakdownTable({ heading, defaultBreakdown, ticketingBreakdown, feePercentage, absorbFees, showTicketing }) {
  if (!defaultBreakdown) return null;
  const hasTicketing = showTicketing && ticketingBreakdown;

  const feeCell = (data) =>
    absorbFees ? `(${formatter.format(data.totalFee)})` : `+${formatter.format(data.totalFee)}`;

  return (
    <div className="mb-3">
      <div className="small font-weight-medium text-uppercase text-muted mb-2">{heading}</div>
      <table className="table table-sm table-borderless mb-0">
        {hasTicketing && (
          <thead>
            <tr>
              <th className="border-0" />
              <th className="border-0 text-right font-weight-medium">Default</th>
              <th className="border-0 text-right font-weight-medium">Ticketing</th>
            </tr>
          </thead>
        )}
        <tbody>
          <tr>
            <td className="border-0 font-weight-medium">Base Price</td>
            <td className="border-0 text-right">{formatter.format(defaultBreakdown.basePrice)}</td>
            {hasTicketing && (
              <td className="border-0 text-right">{formatter.format(ticketingBreakdown.basePrice)}</td>
            )}
          </tr>
          <tr>
            <td className="border-0 text-muted">Fees ({feePercentage}%)</td>
            <td className="border-0 text-right text-muted">{feeCell(defaultBreakdown)}</td>
            {hasTicketing && (
              <td className="border-0 text-right text-muted">{feeCell(ticketingBreakdown)}</td>
            )}
          </tr>
          <tr style={{ borderTop: '1px solid var(--sl-border-secondary)' }}>
            <td className="font-weight-medium">Customer Pays</td>
            <td className="text-right font-weight-bold">{formatter.format(defaultBreakdown.customerPays)}</td>
            {hasTicketing && (
              <td className="text-right font-weight-bold">{formatter.format(ticketingBreakdown.customerPays)}</td>
            )}
          </tr>
          <tr>
            <td className="font-weight-medium">Net to Venue</td>
            <td className="text-right font-weight-bold">{formatter.format(defaultBreakdown.netToVenue)}</td>
            {hasTicketing && (
              <td className="text-right font-weight-bold">{formatter.format(ticketingBreakdown.netToVenue)}</td>
            )}
          </tr>
        </tbody>
      </table>
    </div>
  );
}

export default function PricingSummary() {
  const { membershipProduct, totalFeePercentage } = useMembershipProductShowContext();
  const feePercentage = totalFeePercentage || 0;
  const absorbFees = membershipProduct.absorb_fees ?? true;
  const showTicketing = membershipProduct.add_to_event_ticketing;

  const annualDefault = breakdown(membershipProduct.default_price);
  const annualTicketing = breakdown(membershipProduct.ticketing_price_record);
  const monthlyDefault = breakdown(membershipProduct.default_monthly_price);
  const monthlyTicketing = breakdown(membershipProduct.ticketing_monthly_price_record);

  const hasAnyPricing = annualDefault || monthlyDefault;

  return (
    <div className="team-member-card mb-3 h-100">
      <div className="team-member-card-header">
        <div className="team-member-card-header-left">
          <div className="team-member-card-title-row">
            <h2 className="team-member-card-title">Pricing</h2>
          </div>
        </div>
      </div>
      <div className="team-member-card-body">
        {!hasAnyPricing ? (
          <p className="text-muted mb-0">No pricing configured.</p>
        ) : (
          <>
            {annualDefault && (
              <BreakdownTable
                heading="Annual"
                defaultBreakdown={annualDefault}
                ticketingBreakdown={annualTicketing}
                feePercentage={feePercentage}
                absorbFees={absorbFees}
                showTicketing={showTicketing}
              />
            )}
            {monthlyDefault && (
              <BreakdownTable
                heading="Monthly"
                defaultBreakdown={monthlyDefault}
                ticketingBreakdown={monthlyTicketing}
                feePercentage={feePercentage}
                absorbFees={absorbFees}
                showTicketing={showTicketing}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}
